import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '../routing';
import { revenueApi, formatCurrency, formatCurrencyWhole } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../utils/useIsMobile';
import { missingRangesLabel, entryFrontierLine, isoToday } from '../utils/revenueFormat';
import { formatIsoRangeShort } from '../utils/dateFormat';
import { PRESETS_ANALYTIC, activePresetKey, withPeriodParams } from '../utils/period';
import { categoryLineColor } from '../utils/classificationColors';
import RevenueQuickAddModal from './RevenueQuickAddModal';
import AmberCallout from './AmberCallout';
import EmptyState from './EmptyState';
import RevenueMetricTiles, { PCT_CLASS, ExcludedSpendNote, EntryFrontierText } from './RevenueMetricTiles';
import Icon from './Icon';
import RevenueCalculationExplanation from './RevenueCalculationExplanation';
import FoodCostIcon from './FoodCostIcon';
import ChartTooltip, { useHoverTip } from './ChartTooltip';

// A handful of bubbles rising past the mermaid — the "bath" wink for the
// first-ever visit to this card, before any revenue has been typed in. The
// layer (.revenue-bubbles in index.css) is deliberately wider than the
// mermaid's own box, so bubbles rise around her rather than in a cramped
// column. Pure CSS (transform/opacity only, GPU-composited) — see
// .revenue-bubble in index.css for the keyframes and the
// prefers-reduced-motion opt-out. Six bubbles is a deliberate ceiling: more
// composited layers cost paint for a decoration nobody reads twice.
function RevenueBubbles() {
  const bubbles = [
    { left: '4%',  size: 12, duration: '5.2s', delay: '-1.1s', drift: '9px'  },
    { left: '22%', size: 8,  duration: '4.6s', delay: '-3.4s', drift: '-11px' },
    { left: '40%', size: 17, duration: '6.4s', delay: '-0.4s', drift: '6px'  },
    { left: '58%', size: 9,  duration: '5.8s', delay: '-2.6s', drift: '-9px' },
    { left: '76%', size: 15, duration: '4.9s', delay: '-4.1s', drift: '11px'  },
    { left: '94%', size: 10, duration: '6.9s', delay: '-1.8s', drift: '-6px' },
  ];
  return (
    <span className="revenue-bubbles" aria-hidden="true">
      {bubbles.map((b, i) => (
        <span
          key={i}
          className="revenue-bubble"
          style={{
            left: b.left, width: b.size, height: b.size,
            animationDuration: b.duration, animationDelay: b.delay,
            '--bubble-drift': b.drift,
          }}
        />
      ))}
    </span>
  );
}

// הכנסות מול הוצאות — the second budget widget on /analysis: what the
// restaurant SPENT, ex-VAT, as a share of what it TOOK, per קטגוריה and in
// total. ONE revenue figure is the denominator for every row — that is the
// food-cost question, and it is why a category nobody could type a Z-number for
// no longer falls out of the ratio.
//
// The percentage is ONE division of the two figures in the tiles beside it:
// הוצאות ÷ הכנסות. הוצאות (ללא מע״מ) counts purchasing only on the days of the
// picked range that have revenue entered or are marked closed (owner,
// 2026-09-22 — countedDays in server/lib/revenue.js), so a half-typed month no
// longer inflates the ratio. What was left out is in the ⋮ menu's חשוב לדעת,
// and the amber strip says which days are missing.
//
// This card is deliberately LEAN: a headline figure, a ranked list, one amber
// clause. The full coverage story (bars + day calendar) lives on
// /categories-management's revenue section — the CTA and the "ניהול הכנסות"
// link both go there. The one speed exception: the quick-add modal opens right
// here, prefilled with the first missing range, because "type in yesterday's Z"
// must not cost a navigation.
//
// ── why the category rows carry NO bars ───────────────────────────────────
// They used to. A restaurant's categories land at 4–16% of revenue, and on the
// 0–100% domain those all drew as indistinguishable nubs against ~85% empty
// track — the bar stopped encoding anything and the reader fell back to the
// number, which is what "the visuals aren't clear" meant. Re-fitting the domain
// to the largest category is worse, not better: it makes 16% look like a
// nearly-full bar. A bar needs something to be measured AGAINST, and this card
// has neither a target nor a previous period (both deliberately out of scope).
// So the percentage itself is the visual — 20px, tabular, all rows aligned in
// one column the eye runs straight down. The single surviving bar is the
// headline meter, the only one whose 100% means something real (the income).
//
// Renders above WeeklyBudgetBreakdown, same card grammar, same {dateFrom,
// dateTo} contract (the page's picked range, ISO). Hidden in the network view
// and behind budget_module_enabled by AnalysisPage — and if the flag flips
// off mid-session the API answers 403, which this card treats as "render
// nothing" rather than an error: the module is off, not broken.

export default function RevenueVsBudgetPanel({ dateFrom, dateTo }) {
  const { canEdit } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [gatedOff, setGatedOff] = useState(false);
  const [modal, setModal] = useState(null);   // { dateFrom?, dateTo? }
  const [menuOpen, setMenuOpen] = useState(false);
  const [titleHover, setTitleHover] = useState(false);
  // Same hover readout the budget card's rows use — one component, so the two
  // lists on this page cannot format the same fact two ways. tipHeight is
  // raised from the default because this tip carries a sentence under the
  // figures; the value only decides when the tip flips BELOW a row near the top
  // of the list, and an under-estimate there puts it over the card's title.
  const catRowsRef = useRef(null);
  // Includes the period, Food Cost membership and the ratio explanation.
  const { tip, show: showTip, hide: hideTip } = useHoverTip(catRowsRef, { tipHeight: 165 });
  const rangeLabel = formatIsoRangeShort(dateFrom, dateTo);
  const navigate = useNavigate();
  // Only the amber clause reads this now: the missing DATES are worth naming on
  // a wide card and are the first thing to drop on a phone, where spelling them
  // out wraps the strip to two lines for a message whose actionable half is the
  // button. The hover tooltip this used to drive is gone with the bars — every
  // row now shows its ₪ and its % inline, so there is nothing left to reveal.
  const isMobile = useIsMobile(640);

  const load = useCallback(() => {
    // A cleared range (the date popover's "נקה טווח") is a real, reachable
    // state, and this card cannot answer for it: the ratio divides spend by the
    // revenue of a WINDOW, and an unbounded one has none. It used to just skip
    // the fetch and leave the previous period's numbers on screen — the page
    // header said "no range" while this card still showed July.
    if (!dateFrom || !dateTo) { setSummary(null); setLoading(false); return Promise.resolve(); }
    setLoading(true);
    return revenueApi.summary({ date_from: dateFrom, date_to: dateTo })
      .then(r => { setSummary(r.data); setFailed(false); setGatedOff(false); })
      .catch(e => {
        // 403 = the module was switched off under us. Not an error to report —
        // the widget just goes away until the settings refetch removes it.
        if (e?.response?.status === 403) setGatedOff(true);
        else setFailed(true);
      })
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Same freshness rule as the page's other refetch-on-focus surfaces: revenue
  // is typically entered in another tab (/budget), and coming back here should
  // show it without a manual reload.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  // Above the gated-off early return: a hook after it renders conditionally,
  // which is the "rendered fewer hooks than expected" crash the 403 test caught.
  // A refetch on a period change would otherwise leave a stale index reading one
  // category's figures out under another's name.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { hideTip(); }, [summary]);

  if (gatedOff) return null;

  // The full report, carrying the same window so the two screens describe the
  // same money. `summary` is null on the first paint, so read defensively —
  // `lines` proper is destructured further down, after the loading branches.
  // /analysis's presets, carried as `?period=<key>` when one is active. The
  // page keeps its range current (usePresetPeriod), so matching now is sound.
  const reportQs = withPeriodParams(new URLSearchParams(), activePresetKey(dateFrom, dateTo, PRESETS_ANALYTIC), dateFrom, dateTo);
  const reportPath = `/analysis/reports/revenue${reportQs.toString() ? `?${reportQs}` : ''}`;
  const goToReport = () => navigate(reportPath);
  const hasLines = (summary?.lines?.length ?? 0) > 0;

  const header = (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 16 }}>
      {/* No subtitle (owner): the VAT basis is already on every figure's own
          caption, and the דוח-Z hint lives where the typing happens — the
          /budget section is titled "ניהול הזנת הכנסות (ע״פ דוח Z)". */}
      {/* The title is the door to the full report, same grammar as the two
          sibling cards. With no lines defined there is nothing to report, so
          it stays a plain heading rather than a button leading to an empty
          table. */}
      <div style={{ minWidth: 0 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--ink-muted)', margin: 0 }}>
          {!hasLines ? 'הכנסות מול הוצאות' : (
            <button
              type="button"
              onClick={goToReport}
              onMouseEnter={() => setTitleHover(true)}
              onMouseLeave={() => setTitleHover(false)}
              aria-label="צפייה בדוח המלא של הכנסות מול הוצאות"
              style={{
                font: 'inherit', fontSize: '1rem', fontWeight: 700, color: 'var(--ink-muted)',
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                textDecoration: titleHover ? 'underline' : 'none',
              }}
            >הכנסות מול הוצאות</button>
          )}
        </h3>
        {/* No open-period line here (owner). The סה״כ רכש card at the top of
            the page already says it for the whole window — and it is the card
            that carries the amber calendar marking it, matching the period
            picker. This card is not where a reader learns the month is open. */}
      </div>
      {/* Same ⋮ idiom as WeeklyBudgetBreakdown below — one header grammar down
          the page. The menu carries the door to /budget and the same kind of
          "איך מחושב" note the sibling card carries. */}
      <div className="page-menu" style={{ position: 'relative' }}>
        <button
          className="page-menu-trigger"
          aria-label="פעולות נוספות"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(o => !o)}
        >⋮</button>
        {menuOpen && (
          <>
            <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
            <div className="page-menu-panel" style={{ minWidth: 300 }}>
              <Link
                className="page-menu-item"
                to={reportPath}
                onClick={() => setMenuOpen(false)}
                title="טבלה מלאה, פירוק חודשי ופירוק לפי ספקים"
              >
                <Icon name="list" size={15} /> צפייה בדוח מלא
              </Link>
              {canEdit && (
                <Link
                  className="page-menu-item"
                  to="/categories-management?revenue=1"
                  onClick={() => setMenuOpen(false)}
                  title="הזנת פדיון, לוח כיסוי ורשימת ההזנות"
                >
                  <Icon name="coins" size={15} /> ניהול הזנת הכנסות
                </Link>
              )}
              {canEdit && (
                <Link className="page-menu-item" to="/categories-management?definitions=1"
                  onClick={() => setMenuOpen(false)} title="הגדרת קטגוריות עסק וסימון Food Cost">
                  <Icon name="tag" size={15} /> הגדרת קטגוריות
                </Link>
              )}
              {canEdit && (
                <Link
                  className="page-menu-item"
                  to="/categories-management"
                  onClick={() => setMenuOpen(false)}
                  title="שיוך ספקים וסיווגים לקטגוריות"
                >
                  <Icon name="tag" size={15} /> שיוך הוצאות לקטגוריות
                </Link>
              )}
              <div style={{ borderTop: '1px solid #eef2f7', margin: '6px 0', padding: '10px 4px 0' }}>
                <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>איך מחושב</div>
                <RevenueCalculationExplanation />
              </div>
              <ExcludedSpendNote
                totals={summary?.totals}
                style={{ borderTop: '1px solid #eef2f7', margin: '6px 0 0', padding: '10px 4px 0' }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="card">
        {header}
        <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
      </div>
    );
  }

  // Before the failure branch: a cleared range is not a failure, and saying
  // "cannot load" for it would send someone looking for an outage. See load().
  if (!dateFrom || !dateTo) {
    return (
      <div className="card">
        {header}
        <EmptyState message="בחרו טווח תאריכים כדי לראות את היחס בין ההוצאות להכנסות" />
      </div>
    );
  }

  if (failed || !summary) {
    return (
      <div className="card">
        {header}
        <div style={{ color: '#94a3b8', fontSize: 13.5 }}>לא ניתן לטעון את נתוני ההכנסות כרגע.</div>
      </div>
    );
  }

  const { lines, totals, unmapped, vat_rate: vatRate, countable_days: countableDays } = summary;
  // ONE revenue figure for the restaurant — not "does any category have some".
  const anyRevenue = Boolean(totals.has_revenue);

  const quickAdd = (opts = {}) => {
    const first = totals.missing_ranges?.[0];
    setModal({
      dateFrom: opts.dateFrom ?? first?.from ?? dateFrom,
      dateTo: opts.dateTo ?? first?.to ?? dateTo,
    });
  };

  const modalEl = modal && (
    <RevenueQuickAddModal
      dateFrom={modal.dateFrom}
      dateTo={modal.dateTo}
      vatRate={vatRate}
      onSaved={load}
      onClose={() => setModal(null)}
    />
  );

  // ── empty states (spec §6.1.6) ────────────────────────────────────────────

  if (lines.length === 0) {
    return (
      <div className="card">
        {header}
        <p style={{ color: 'var(--ink-muted)', fontSize: 13.5, margin: '0 0 12px', maxWidth: '62ch' }}>
          כדי למדוד הוצאות מול הכנסות, הגדירו קודם קטגוריות - מזון, משקאות, תפעול.
        </p>
        {canEdit && (
          <Link className="btn btn-primary" to="/categories-management">הגדרת קטגוריות</Link>
        )}
      </div>
    );
  }

  if (countableDays === 0) {
    return (
      <div className="card">
        {header}
        <div style={{ color: '#94a3b8', fontSize: 13.5 }}>הטווח שנבחר בעתיד - עדיין אין ימים למדוד.</div>
      </div>
    );
  }

  if (!anyRevenue) {
    // The shared welcome moment (mermaid + gentle rise-in) — the same grammar
    // ClassificationSpendEmptyState/SupplierSpendEmptyState already use elsewhere on
    // this page. This card was the odd one out with plain paragraphs; benefit-
    // first copy + the mermaid is most of "more inviting" for free.
    return (
      <div className="card">
        {header}
        {modalEl}
        <EmptyState message="כאן יופיע היחס בין ההוצאות להכנסות" accent={<RevenueBubbles />}>
          <p className="empty-subline">
            הזינו את הפדיון שלכם (כולל מע״מ, לפי דוח ה-Z) - ותוכלו לראות לכל
            קטגוריה את האחוז שלו מההכנסות.
          </p>
          <p style={{ color: '#94a3b8', fontSize: 12.5, margin: '10px 0 0' }}>
            ההוצאות בטווח כבר מחכות: {formatCurrency(totals.total_purchases_net ?? totals.spend_net_total)} (ללא מע״מ)
          </p>
          {canEdit && (
            <button type="button" className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => quickAdd()}>
              הזנת הפדיון הראשון
            </button>
          )}
        </EmptyState>
      </div>
    );
  }

  // ── the working card ──────────────────────────────────────────────────────

  const showBanner = totals.missing_days_count > 0 && countableDays > 0;

  // The "how far does my revenue entry reach" line — rules shared with the
  // report page via entryFrontierLine (utils/revenueFormat.js).
  const frontierLine = entryFrontierLine({
    frontier: summary.entry_frontier, hasRevenue: totals.has_revenue, dateTo, bannerShowing: showBanner,
    today: isoToday(),
  });


  // Every category, biggest first, and unmapped spend pinned to the END of the
  // SAME list wearing the SAME row grammar (owner: "it should look like תפעול
  // and the rest"). It is not a block of its own and it is not amber: a reader
  // scanning straight down the percentage column must not hit a different shape
  // at the bottom. Muted ink is the whole of the distinction, and it is enough —
  // the row still reads as "not one of your categories".
  const rows = [...lines]
    .sort((a, b) => b.spend_net_total - a.spend_net_total)
    .map(l => ({
      key: `line-${l.category_line_id}`,
      name: l.category_line_name,
      color: l.color,
      net: l.spend_net_total,
      pct: l.pct_of_revenue,
      isFoodCost: l.is_food_cost,
    }));
  const maxRowPct = Math.max(0, ...rows.map(r => r.pct ?? 0));
  // Non-zero, not positive: under the counted-days rule a credit on a counted
  // day can outweigh its invoice on an excluded one, leaving unmapped spend
  // NEGATIVE. It stays visible as information, outside הוצאות.
  if (unmapped && unmapped.spend_net_total !== 0) {
    rows.push({
      key: 'unmapped',
      name: 'ללא שיוך',
      color: '#cbd5e1',
      net: unmapped.spend_net_total,
      pct: unmapped.pct_of_revenue,
      muted: true,
      to: canEdit ? '/categories-management' : null,
    });
  }

  // Resolved from the index the hover stored, AFTER `rows` is assembled — the
  // unmapped row is pushed on here, so a tip opened over it would read out of
  // bounds against the sorted lines alone.
  const activeRow = tip ? (rows[tip.i] ?? null) : null;

  return (
    <div className="card">
      {header}
      {modalEl}

      {/* The same three tiles the full report opens on, from the same
          component (owner, from a customer review): card and report lead with
          identical figures, so moving between them costs no re-reading — and
          they cannot drift apart.
          No ≈ on the percentage any more: it divides counted days only, so
          missing days no longer bias it — they just leave spend out, which
          the grey line under the tiles states. */}
      {/* Agorot, not whole shekels — these are the card's tie-out totals.
          formatCurrencyWhole's own rule says
          which side these belong on: it is for figures "being COMPARED rather
          than reconciled", and "anything an accountant might tie out keeps
          formatCurrency". These three tiles are the card's tie-out totals. The
          category rows BELOW stay whole — those are the compared ones. */}
      <RevenueMetricTiles
        totals={totals}
        money={formatCurrency}
      />

      {/* ── by category ─────────────────────────────────────────────────────
          Naming the unit ONCE in a column header is what lets every row below
          drop its own "% מההכנסות" caption and shrink from three lines to one. */}
      {/* Layout lives in index.css (.cat-rows-head): a two-ended flex on a
          phone, and the SAME grid as .cat-row from 641px so each name sits in
          its own column rather than being aligned by hand. The three spacers
          are the columns that have no name — swatch, bar, ₪ — and they are what
          make the grid line up; they are display:none on a phone, where the
          header is a flex and five children would break it.
          Nothing here may set display/justify inline: an inline style outranks
          the media query and would pin the header to flex at every width. */}
      <div className="cat-rows-head" style={{
        borderTop: '1px solid #eef1f5', marginTop: 18, padding: '12px 0 4px',
      }}>
        <span className="cat-swatch cat-head-spacer" aria-hidden="true" />
        {/* Both headers, one treatment: they name the two columns of the same
            table, so a muted second one read as a caption on the first rather
            than as its pair (owner). */}
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>קטגוריה</span>
        <span className="cat-head-spacer" aria-hidden="true" />
        <span className="cat-head-spacer" aria-hidden="true" />
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>% מההכנסות</span>
      </div>

      <div className="cat-rows" ref={catRowsRef}>
        {/* The largest row, for the desktop share bar. It normalises to the
            BIGGEST CATEGORY, not to 100% of revenue — a restaurant whose top
            category is 12% of revenue would otherwise get six identical stubs.
            Unmapped is excluded from the scale and draws no bar at all: it is
            not a category, and leaving it out is a fourth way of saying so that
            costs nothing. */}
        {rows.map((r, i) => {
          const barPct = maxRowPct > 0 && r.pct != null && !r.muted
            ? Math.max(2, (r.pct / maxRowPct) * 100)
            : null;
          const inner = (
            <>
              {/* 10px rounded SQUARE, the same swatch WeeklyBudgetBreakdown and
                  ClassificationAnalysis draw — not a circle. Matching it is what
                  tells the eye these three lists are one family, and it is the
                  only thing tying a row here to the same category's slice on the
                  cards above — which is why a colourless line falls back to the
                  CATEGORY palette here, exactly as it does on those cards, and
                  no longer to a one-off slate that matched nothing. (A legacy
                  colourless line can still land on a different palette entry per
                  card, since the fallback is by list position and the lists sort
                  differently; every line created since colours-on-create has a
                  stored colour, which is what actually ties them.) */}
              <span aria-hidden="true" className="cat-swatch" style={{ background: r.color || categoryLineColor(r, i) }} />
              <span className={`cat-name${r.muted ? '' : ' cat-name-with-marker'}`} style={{ color: r.muted ? 'var(--ink-muted)' : 'var(--ink)' }}>
                {r.muted ? r.name : <span className="cat-name-text">{r.name}</span>}
                {r.isFoodCost && <FoodCostIcon />}
                {r.muted && <span className="unmapped-outside-total">לא נכלל בהוצאות</span>}
              </span>
              {/* Desktop only. At 375px there is no room and the number is
                  already beside the name; at 1288px the middle of the row was
                  dead space between a name and the figure that belongs to it,
                  which in RTL is the LAST thing in the scan path. A bar is what
                  that space is for. */}
              <span className="cat-bar" aria-hidden="true">
                {barPct != null && (
                  <span className="cat-bar-fill" style={{ width: `${barPct}%`, background: r.color || categoryLineColor(r, i) }} />
                )}
              </span>
              {/* The ₪ rides on the name's line on a phone — it is the
                  actionable magnitude, but the percentage is the question being
                  asked. At desktop it gets its own end-aligned column, so the
                  amounts line up down the card. */}
              <span className="cat-amount">{formatCurrencyWhole(r.net)}</span>
              {/* Same .pct-figure as the tile above it. The parts must not render
                  larger than the whole they divide into — they are the same
                  measurement at two levels, so a bigger row read as the more
                  important number. Rank is carried by the tile's container and
                  label, never by size.
                  No amber here either: a single category above 100% of revenue
                  is pathological, and the rule existed only to generate a
                  fourth treatment. */}
              <span className={`${PCT_CLASS} cat-pct`} style={{ color: r.muted ? 'var(--ink-muted)' : 'var(--ink)' }}>
                {/* Only reachable when no revenue has been typed at all — a
                    state the empty card above already explains, so a dash
                    rather than a second call to action. */}
                {r.pct != null ? `${r.pct}%` : '-'}
              </span>
            </>
          );
          // "Not a category" is said on ONE axis — colour, on the whole row —
          // plus a structural break above it. It used to be said on three at
          // once (smaller, lighter, greyer), and three signals of one thing
          // read as three unrelated decisions, which is most of what "the
          // percentages all look different" meant.
          const cls = `cat-row${r.muted ? ' is-muted' : ''}`;
          // Desktop only, exactly as the budget card gates its own rows: on a
          // phone there is no pointer, the row already prints both figures, and
          // a tip anchored to a 48px row would cover its neighbours.
          const hover = isMobile ? {} : {
            onMouseEnter: (e) => showTip(i, e.currentTarget),
            onMouseLeave: hideTip,
          };
          return r.to ? (
            <Link key={r.key} to={r.to} aria-label="שיוך הוצאות לקטגוריות" className={cls} {...hover}>{inner}</Link>
          ) : (
            <div key={r.key} className={cls} {...hover}>{inner}</div>
          );
        })}

        {activeRow && (
          <ChartTooltip
            tip={tip}
            title={activeRow.name}
            rows={[
              // The range these two figures cover. The page states it once at
              // the top, but a readout that quotes exact ₪ without saying what
              // window they belong to is the one place that omission bites.
              ...(rangeLabel ? [{ label: 'תקופה', value: rangeLabel }] : []),
              // The row prints whole shekels; the readout is where the exact
              // figure lives, same division of labour as the budget card.
              // The basis rides on BOTH money values rather than being stated
              // once: two ₪ figures where only one is qualified reads as though
              // the other is the gross one.
              { label: 'הוצאות', value: `${formatCurrency(activeRow.net)} (ללא מע״מ)` },
              { label: 'מתוך הכנסות', value: `${formatCurrency(totals.revenue_net)} (ללא מע״מ)` },
              { label: 'Food Cost', value: activeRow.isFoodCost ? 'חלק מה-Food Cost' : 'לא חלק מה-Food Cost' },
            ]}
            // The sentence the column header cannot carry. A percentage beside a
            // name states a ratio without ever naming its two sides, which is
            // most of what makes this table read as arithmetic homework.
            footnote={activeRow.pct == null ? null : (activeRow.muted
              ? `הוצאות שטרם שויכו לקטגוריה מהוות ${activeRow.pct}% מההכנסות`
              : `עלות ${activeRow.name} מהווה ${activeRow.pct}% מההכנסות`)}
          />
        )}
      </div>

      {/* The card's ONE amber element, in the place it has always sat: under
          the rows, above the footer. Condition, then consequence — the spend
          of those days is not in the figures above until revenue is entered.
          It must hold ONE LINE on a phone (owner): a 343px card minus the
          heart, the gaps and the button leaves ~200px for the message, and a
          strip that wraps to two rows for a one-clause message looks broken
          next to the tight list above it. So the phone gets the short copy AND
          the short button label ("הזנה" — the strip already says what is
          missing, so the verb alone is unambiguous), while a wide card can
          afford to name the dates and spell the button out. */}
      {showBanner && (
        <AmberCallout
          actionLabel={canEdit ? (isMobile ? 'הזנה' : 'הזנת הכנסות') : undefined}
          onAction={() => quickAdd()}
        >
          {/* One sentence, cause then consequence — the same wording as the
              report's notice (owner: "חסרים N ימי דיווח - הוצאות מימים אלה…"
              read as two fragments glued with a dash). A phone keeps only the
              cause so the strip holds ONE line; the button says the rest. */}
          {totals.missing_days_count === 1
            ? <>ליום אחד לא הוזנו הכנסות</>
            : <>ל-{totals.missing_days_count} ימים לא הוזנו הכנסות</>}
          {!isMobile && <> ({missingRangesLabel(totals.missing_ranges)})</>}
          {!isMobile && (totals.missing_days_count === 1
            ? ', ולכן ההוצאות שלו לא נכללו בחישוב'
            : ', ולכן ההוצאות שלהם לא נכללו בחישוב')}
        </AmberCallout>
      )}

      {/* Footer. The VAT basis moved onto the two figures themselves (owner),
          so restating it here would be its third printing in one card. The
          "לצפייה" chip keeps the exact shape the sibling cards
          (SupplierDonutCard, ClassificationAnalysis) close on, so a reader
          scanning three cards for "how do I open the full report" finds the
          same thing in the same place on all three. */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        gap: 10, marginTop: 12, flexWrap: 'wrap',
      }}>
        {/* How far revenue entry actually reaches — a customer asked to see it
            without leaving this card, to tell whether they have typed in their
            latest takings.
            FOOTER metadata, not body content. It first shipped between the
            tiles and the table, where it read as clutter for two reasons worth
            not repeating: that gap exists to SEPARATE two blocks, so a line
            parked in it belongs to neither and interrupts the tiles → header →
            rows motion; and sitting directly under `% מההכנסות` the eye took it
            for a second, greyer disclaimer on the headline ratio. Here it is a
            provenance stamp on a row that already exists, costing no vertical
            rhythm, as far from the ratio as the card allows.
            It cannot be mistaken for the amber strip above it either — that one
            is a washed container, amber, 500-weight and a full-width tap
            target; this is uncontained, grey, inert, and shares a row with a
            control. Order is act-then-inform. */}
        {frontierLine && (
          <EntryFrontierText line={frontierLine} style={{ marginInlineEnd: 'auto', minWidth: 0, fontSize: 12.5 }} />
        )}
        {hasLines && (
          <button
            type="button"
            onClick={goToReport}
            aria-label="צפייה בדוח המלא של הכנסות מול הוצאות"
            style={{
              display: 'inline-flex', alignItems: 'center', minHeight: 44,
              padding: '6px 4px 6px 0', background: 'none', border: 'none', cursor: 'pointer',
            }}
          >
            <span className="btn btn-sm btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, pointerEvents: 'none' }}>
              לצפייה <Icon name="chevron" size={15} style={{ transform: 'rotate(90deg)' }} />
            </span>
          </button>
        )}
      </div>
    </div>
  );
}